/**
 * Cross-island avatar sync.
 *
 * Both sidebars are `turbo_permanent`, so they outlive the page that changed
 * the avatar and never re-read their server props — the thumbnail would stay
 * stale until a full reload. The account page publishes the saved URL; the
 * sidebars subscribe.
 *
 * The published URL is always the persisted ActiveStorage one, never the
 * upload's local object URL: the account island revokes that on unmount, and
 * a permanent sidebar holding a revoked blob would show a broken image.
 */

import { useEffect, useState } from 'react';

export const AVATAR_UPDATED_EVENT = 'avatar:updated';

export function publishAvatarUpdated(avatarUrl: string) {
  document.dispatchEvent(new CustomEvent(AVATAR_UPDATED_EVENT, { detail: { avatarUrl } }));
}

/** The live avatar URL: the server's prop until an upload publishes a newer one. */
export function useLiveAvatarUrl(initialUrl?: string | null): string | null | undefined {
  const [avatarUrl, setAvatarUrl] = useState(initialUrl);

  // A fresh server render wins — it is newer than anything published earlier.
  useEffect(() => {
    setAvatarUrl(initialUrl);
  }, [initialUrl]);

  useEffect(() => {
    function handleUpdate(event: Event) {
      const { avatarUrl: updated } = (event as CustomEvent<{ avatarUrl?: string }>).detail ?? {};
      if (updated) setAvatarUrl(updated);
    }

    document.addEventListener(AVATAR_UPDATED_EVENT, handleUpdate);
    return () => document.removeEventListener(AVATAR_UPDATED_EVENT, handleUpdate);
  }, []);

  return avatarUrl;
}
